import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { configService } from '../services/ConfigService';
import '../styles/StaffBookingForm.css';

function StaffBookingForm() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [viewMode, setViewMode] = useState<'menu' | 'add' | 'list'>('menu');

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [cities, setCities] = useState<any[]>([]);
  const [booths, setBooths] = useState<any[]>([]);
  const [upcomingBookings, setUpcomingBookings] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    customerName: '',
    email: '',
    phone: '',
    boothType: 'classic',
    boothDisplayName: 'كابينة Classic',
    eventDate: '',
    eventTime: '20:00', // Default time 8:00 PM
    location: '',
    bookingType: 'hourly',
    hours: 2,
    totalTickets: 0,
    city: '',
    cityFee: 0,
    discount: 0,
    totalAmount: 0,
    paidAmount: 0,
    isManualTotal: false // Track if user manually overrode the total
  });

  useEffect(() => {
    // Check if previously authenticated
    const savedToken = localStorage.getItem('staffToken');
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCities();
      fetchConfig();
    }
  }, [isAuthenticated]);

  const handleLogout = () => {
    localStorage.removeItem('staffToken');
    setToken(null);
    setIsAuthenticated(false);
    setViewMode('menu');
  };

  const fetchCities = async () => {
    if (!token) return;
    try {
      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5002').replace(/\/$/, '');
      const response = await fetch(`${baseUrl}/api/cities`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const activeCities = data.cities || [];
        setCities(activeCities);
        if (activeCities.length > 0 && !formData.city) {
          setFormData(prev => ({
            ...prev,
            city: activeCities[0]._id,
            cityFee: activeCities[0].additionalPrice || 0
          }));
        }
      } else if (response.status === 401 || response.status === 403) {
        handleLogout();
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchConfig = async () => {
    try {
      await configService.refreshConfig();
      const data = configService.getConfig();
      if (data.booths && data.booths.length > 0) {
        const enabledBooths = data.booths.filter((b: any) => b.enabled !== false);
        setBooths(enabledBooths);
        if (!formData.boothType || formData.boothType === 'classic') {
          setFormData(prev => ({
            ...prev,
            boothType: enabledBooths[0].slug,
            boothDisplayName: enabledBooths[0].name
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchUpcomingBookings = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5002').replace(/\/$/, '');
      const response = await fetch(`${baseUrl}/api/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Filter future bookings only and sort by date
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        // Filter future bookings that are confirmed only, and sort by date
        const future = data.bookings.filter((b: any) => new Date(b.eventDate) >= today && b.status === 'confirmed');
        future.sort((a: any, b: any) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime());
        setUpcomingBookings(future);
      } else if (response.status === 401 || response.status === 403) {
        handleLogout();
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCalculatedBasePrice = () => {
    const config = configService.getConfig();
    let basePrice = 0;

    const selectedBooth = config.booths?.find((b: any) => b.slug === formData.boothType);
    const isCustomPricing = selectedBooth?.customPricing?.enabled;

    if (formData.bookingType === 'perPhoto') {
      basePrice = isCustomPricing && selectedBooth?.customPricing?.ticketSystem
          ? (selectedBooth.customPricing.ticketSystem.installationFee || config.ticketSystem?.installationFee || 0)
          : (config.ticketSystem?.installationFee || 0);
    } else {
      const packageList = isCustomPricing && selectedBooth?.customPricing?.hourlyPackages && selectedBooth.customPricing.hourlyPackages.length > 0
          ? selectedBooth.customPricing.hourlyPackages
          : (config.hourlyPackages || []);
      const selectedPackage = packageList?.find((p: any) => p.hours === formData.hours);
      basePrice = selectedPackage ? selectedPackage.price : 0;
    }
    
    return basePrice;
  };

  // Recalculate price when dependencies change (only if not manually overridden)
  useEffect(() => {
    if (!isAuthenticated || formData.isManualTotal || viewMode !== 'add') return;
    try {
      const calculatedBasePrice = getCalculatedBasePrice();
      const discountPercentage = formData.discount || 0;
      const discountVal = (calculatedBasePrice * discountPercentage) / 100;
      const total = calculatedBasePrice + (formData.cityFee || 0) - discountVal;

      setFormData(prev => ({
        ...prev,
        totalAmount: total,
        paidAmount: prev.paidAmount > 0 ? prev.paidAmount : 0 // Keep deposit if already set
      }));
    } catch (error) {
      console.warn("Could not calculate price:", error);
    }
  }, [formData.bookingType, formData.hours, formData.cityFee, formData.discount, formData.boothType, isAuthenticated, viewMode, formData.isManualTotal]);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);
    try {
      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5002').replace(/\/$/, '');
      const response = await fetch(`${baseUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      if (response.ok && data.token) {
        setIsAuthenticated(true);
        setToken(data.token);
        localStorage.setItem('staffToken', data.token);
      } else {
        setAuthError(data.message || 'بيانات الدخول غير صحيحة');
      }
    } catch (error) {
      setAuthError('حدث خطأ في الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    
    // 1. تحويل الأرقام العربية إلى إنجليزية
    const arabicNumbers = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    for (let i = 0; i < 10; i++) {
      val = val.replace(new RegExp(arabicNumbers[i], 'g'), i.toString());
    }
    
    // 2. إزالة الفراغات والشرطات
    val = val.replace(/[\s-]/g, '');
    
    // 3. استبدال مفتاح الخط السعودي بصفر
    if (val.startsWith('+966')) {
      val = '0' + val.substring(4);
    } else if (val.startsWith('00966')) {
      val = '0' + val.substring(5);
    } else if (val.startsWith('966')) {
      val = '0' + val.substring(3);
    }
    
    // 4. إزالة أي حروف أو رموز غير رقمية متبقية
    val = val.replace(/[^\d]/g, '');

    setFormData({...formData, phone: val});
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5002').replace(/\/$/, '');
      
      const calculatedBasePrice = getCalculatedBasePrice();
      const discountAmountVal = formData.isManualTotal 
        ? 0 // If manual total, we don't calculate formal discount amount from base
        : (calculatedBasePrice * (formData.discount || 0)) / 100;

      // Base amount will just be totalAmount + discountAmount if not manual
      // If manual, baseAmount is same as totalAmount
      const payload = {
        ...formData,
        baseAmount: formData.isManualTotal ? formData.totalAmount : formData.totalAmount + discountAmountVal,
        discountAmount: discountAmountVal,
        discountCode: formData.discount > 0 ? 'خصم إداري خاص' : undefined,
        hours: formData.bookingType === 'hourly' ? formData.hours : undefined,
        totalTickets: formData.bookingType === 'perPhoto' ? formData.totalTickets : undefined,
        sendEmail: true
      };

      const response = await fetch(`${baseUrl}/api/bookings/admin/manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSuccess(true);
      } else if (response.status === 401 || response.status === 403) {
        alert('انتهت الجلسة. يرجى تسجيل الدخول مرة أخرى.');
        handleLogout();
      } else {
        const errorData = await response.json();
        alert('حدث خطأ أثناء إضافة الحجز: ' + (errorData.message || 'Unknown'));
      }
    } catch (error) {
      console.error(error);
      alert('فشل الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  // ---------------- Render Views ---------------- //

  if (!isAuthenticated) {
    return (
      <div className="staff-container">
        <div className="auth-card">
          <h2>نظام إدارة الحجوزات</h2>
          <p>أدخل حساب الموظف للوصول</p>
          <form onSubmit={handleLoginSubmit}>
            <div className="form-group" style={{textAlign: 'right'}}>
              <label>البريد الإلكتروني</label>
              <input 
                type="email" 
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                style={{textAlign: 'left'}}
                required
              />
            </div>
            <div className="form-group" style={{textAlign: 'right'}}>
              <label>كلمة المرور</label>
              <input 
                type="password" 
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="********"
                style={{textAlign: 'left', letterSpacing: '2px'}}
                required
              />
            </div>
            {authError && <p style={{color: '#ef4444', marginTop: 0, fontWeight: 'bold'}}>{authError}</p>}
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'جاري التحقق...' : 'تسجيل الدخول'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (viewMode === 'menu') {
    return (
      <div className="staff-container">
        <div className="auth-card" style={{ maxWidth: '600px' }}>
          <h2>بوابة الموظفين</h2>
          <p>مرحباً بك، الرجاء اختيار العملية المطلوبة</p>
          <div className="options-grid">
            <div className="option-card" onClick={() => setViewMode('add')}>
              <h3>إضافة حجز جديد</h3>
              <p>إنشاء حجز يدوي لعميل جديد</p>
            </div>
            <div className="option-card" onClick={() => {
              setViewMode('list');
              fetchUpcomingBookings();
            }}>
              <h3>الحجوزات القادمة</h3>
              <p>عرض ومتابعة الحجوزات المستقبلية</p>
            </div>
          </div>
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <button className="btn-secondary" onClick={handleLogout} style={{ padding: '8px 16px', fontSize: '14px', borderColor: '#ef4444', color: '#ef4444' }}>
              تسجيل الخروج
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (viewMode === 'list') {
    return (
      <div className="staff-container" style={{ alignItems: 'flex-start' }}>
        <div className="main-card wide">
          <div className="card-header">
            <h2>الحجوزات القادمة</h2>
            <div className="header-actions">
              <button className="btn-secondary" onClick={() => setViewMode('menu')}>عودة للقائمة</button>
              <button className="btn-primary" onClick={fetchUpcomingBookings} disabled={loading}>
                {loading ? 'جاري التحديث...' : 'تحديث'}
              </button>
            </div>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>اسم العميل</th>
                    <th>رقم الحجز</th>
                    <th>تاريخ المناسبة</th>
                    <th>رقم الهاتف</th>
                    <th>نوع البوث</th>
                    <th>النظام</th>
                    <th>المدة/التذاكر</th>
                    <th>المدينة</th>
                    <th>الموقع</th>
                    <th>المدفوع</th>
                    <th>المتبقي</th>
                    <th>الإجمالي</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingBookings.length === 0 ? (
                    <tr><td colSpan={12} style={{textAlign: 'center', padding: '30px'}}>لا توجد حجوزات قادمة</td></tr>
                  ) : (
                    upcomingBookings.map((b: any) => {
                      const cityObj = cities.find(c => c._id === b.city);
                      const displayCity = cityObj ? cityObj.name : (b.cityName || b.city);
                      const eventDate = new Date(b.eventDate);
                      const gregorian = eventDate.toLocaleDateString('en-GB');
                      const hijri = new Intl.DateTimeFormat('ar-SA-u-ca-islamic', {day: 'numeric', month: 'long', year: 'numeric'}).format(eventDate);
                      
                      return (
                        <tr key={b._id}>
                          <td><strong>{b.customerName}</strong></td>
                          <td>{b.bookingCode}</td>
                          <td>
                            <div dir="ltr" style={{textAlign: 'right'}}>{gregorian} {b.eventTime}</div>
                            <div style={{fontSize: '12px', color: '#64748b'}}>{hijri}</div>
                          </td>
                          <td dir="ltr" style={{textAlign: 'right'}}>{b.phone}</td>
                          <td>{b.boothDisplayName || b.boothType}</td>
                          <td>{b.bookingType === 'hourly' ? 'ساعات' : 'تذاكر'}</td>
                          <td>{b.bookingType === 'hourly' ? `${b.hours} ساعة` : `${b.totalTickets} تذكرة`}</td>
                          <td>{displayCity}</td>
                          <td>{b.location}</td>
                          <td style={{color: '#166534'}}>{b.paidAmount || 0}</td>
                          <td style={{color: '#b91c1c'}}>{Math.max(0, (b.totalAmount || 0) - (b.paidAmount || 0))}</td>
                          <td><strong>{b.totalAmount || 0}</strong></td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Add Booking View ---
  if (success) {
    return (
      <div className="staff-container">
        <div className="auth-card">
          <h2 style={{color: '#166534', fontSize: '32px'}}>✓</h2>
          <h2>تم اعتماد الحجز بنجاح</h2>
          <p>تم إضافة الحجز بنجاح وإرسال التأكيد للعميل (في حال التفعيل).</p>
          <div className="header-actions" style={{justifyContent: 'center'}}>
            <button className="btn-secondary" onClick={() => setViewMode('menu')}>القائمة الرئيسية</button>
            <button className="btn-primary" onClick={() => window.location.reload()}>إضافة حجز جديد</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="staff-container">
      <div className="main-card">
        <div className="card-header">
          <h2>إضافة حجز يدوياً</h2>
          <button className="btn-secondary" style={{padding: '8px 16px', fontSize: '14px'}} onClick={() => setViewMode('menu')}>إلغاء</button>
        </div>

        <div className="card-body">
          <div className="form-layout-wrapper">
            <div className="form-content-area">
              <div className="stepper">
            <span className={`step-item ${step >= 1 ? 'active' : ''}`}>1. بيانات العميل</span>
            <span className={`step-item ${step >= 2 ? 'active' : ''}`}>2. تفاصيل المناسبة</span>
            <span className={`step-item ${step >= 3 ? 'active' : ''}`}>3. الباقة والدفع</span>
            <span className={`step-item ${step >= 4 ? 'active' : ''}`}>4. الاعتماد</span>
          </div>

          {step === 1 && (
            <div>
              <div className="form-group">
                <label>اسم العميل *</label>
                <input type="text" className="form-control" value={formData.customerName} onChange={e => setFormData({...formData, customerName: e.target.value})} placeholder="الاسم الثلاثي" />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>رقم الجوال *</label>
                  <input type="tel" className="form-control" dir="ltr" value={formData.phone} onChange={handlePhoneChange} placeholder="05xxxxxxxx" />
                </div>
                <div className="form-group">
                  <label>البريد الإلكتروني *</label>
                  <input type="email" className="form-control" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="example@email.com" />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="form-grid">
                <div className="form-group">
                  <label>تاريخ المناسبة *</label>
                  <input type="date" className="form-control" value={formData.eventDate} onChange={e => setFormData({...formData, eventDate: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>وقت الحضور *</label>
                  <input type="time" className="form-control" value={formData.eventTime} onChange={e => setFormData({...formData, eventTime: e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label>نوع الكابينة *</label>
                <select className="form-control" value={formData.boothType} onChange={e => {
                  const selectedBooth = booths.find(b => b.slug === e.target.value);
                  setFormData({...formData, boothType: e.target.value, boothDisplayName: selectedBooth?.name || e.target.value});
                }}>
                  {booths.map(b => (
                    <option key={b._id || b.slug} value={b.slug}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>المدينة *</label>
                  <select className="form-control" value={formData.city} onChange={e => {
                    const selectedCity = cities.find(c => c._id === e.target.value);
                    setFormData({...formData, city: e.target.value, cityFee: selectedCity?.additionalPrice || 0});
                  }}>
                    {cities.map(c => (
                      <option key={c._id} value={c._id}>{c.name} {c.additionalPrice > 0 ? `(+${c.additionalPrice})` : ''}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>الموقع / القاعة *</label>
                  <input type="text" className="form-control" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} placeholder="اسم القاعة أو الموقع" />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <div className="form-grid">
                <div className="form-group">
                  <label>نوع الحجز *</label>
                  <select className="form-control" value={formData.bookingType} onChange={e => setFormData({...formData, bookingType: e.target.value as any})}>
                    <option value="hourly">ساعات</option>
                    <option value="perPhoto">تذاكر (بالصورة)</option>
                  </select>
                </div>
                {formData.bookingType === 'hourly' ? (
                  <div className="form-group">
                    <label>عدد الساعات *</label>
                    <select className="form-control" value={formData.hours} onChange={e => setFormData({...formData, hours: Number(e.target.value)})}>
                      <option value={2}>ساعتين</option>
                      <option value={3}>3 ساعات</option>
                      <option value={4}>4 ساعات</option>
                      <option value={5}>5 ساعات</option>
                      <option value={6}>6 ساعات</option>
                      <option value={7}>7 ساعات</option>
                      <option value={8}>8 ساعات</option>
                    </select>
                  </div>
                ) : (
                  <div className="form-group">
                    <label>عدد التذاكر الأساسية *</label>
                    <input type="number" className="form-control" value={formData.totalTickets} onChange={e => setFormData({...formData, totalTickets: Number(e.target.value)})} />
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>نسبة الخصم الإداري (%)</label>
                <input type="number" min={0} max={100} className="form-control" value={formData.discount} onChange={e => setFormData({...formData, discount: Number(e.target.value), isManualTotal: false})} placeholder="مثال: 15" />
              </div>
              <hr style={{border: 'none', borderTop: '1px solid #e1e4e8', margin: '30px 0'}} />
              
              <div className="form-grid">
                <div className="form-group">
                  <label>إجمالي المبلغ (قابل للتعديل يدوياً) *</label>
                  <input type="number" className="form-control" value={formData.totalAmount} onChange={e => setFormData({...formData, totalAmount: Number(e.target.value), isManualTotal: true})} />
                  <small style={{color: '#64748b', fontSize: '12px'}}>السعر يُحسب تلقائياً ولكن يمكنك تعديله بشكل يدوي هنا.</small>
                </div>
                <div className="form-group">
                  <label>المبلغ المدفوع (العربون) *</label>
                  <input type="number" className="form-control" value={formData.paidAmount} onChange={e => setFormData({...formData, paidAmount: Number(e.target.value)})} />
                  <small style={{color: '#64748b', fontSize: '12px'}}>أدخل صفر إذا لم يدفع العميل أي مبلغ بعد.</small>
                </div>
              </div>
            </div>
          )}

          {step === 4 && (() => {
            const selectedCityObj = cities.find(c => c._id === formData.city);
            const displayCityName = selectedCityObj ? selectedCityObj.name : formData.city;
            
            return (
            <div>
              <div className="summary-box">
                <div className="summary-row">
                  <span>اسم العميل</span>
                  <strong>{formData.customerName}</strong>
                </div>
                <div className="summary-row">
                  <span>رقم الجوال</span>
                  <strong dir="ltr">{formData.phone}</strong>
                </div>
                <div className="summary-row">
                  <span>تاريخ ووقت المناسبة</span>
                  <strong>{formData.eventDate} - {formData.eventTime}</strong>
                </div>
                <div className="summary-row">
                  <span>المدينة والكابينة</span>
                  <strong>{displayCityName} - {formData.boothDisplayName}</strong>
                </div>
                {formData.discount > 0 && !formData.isManualTotal && (
                  <div className="summary-row">
                    <span>نسبة الخصم الإداري</span>
                    <strong style={{color: '#e74c3c'}}>{formData.discount}%</strong>
                  </div>
                )}
                <div className="summary-row total">
                  <span>إجمالي المبلغ المستحق</span>
                  <span>{formData.totalAmount} ر.س</span>
                </div>
                <div className="summary-row">
                  <span>المبلغ المدفوع (العربون)</span>
                  <span style={{color: '#166534'}}>{formData.paidAmount} ر.س</span>
                </div>
                <div className="summary-row">
                  <span>المبلغ المتبقي</span>
                  <span style={{color: '#b91c1c'}}>{Math.max(0, formData.totalAmount - formData.paidAmount)} ر.س</span>
                </div>
              </div>
            </div>
            );})()}
          </div>
          
          <div className="side-summary-area">
            <div className="side-summary-title">ملخص الحجز</div>
            <div className="side-summary-item">
              <span>العميل:</span>
              <strong>{formData.customerName || '—'}</strong>
            </div>
            <div className="side-summary-item">
              <span>تاريخ المناسبة:</span>
              <strong dir="ltr">{formData.eventDate || '—'}</strong>
            </div>
            <div className="side-summary-item">
              <span>الكابينة:</span>
              <strong>{formData.boothDisplayName}</strong>
            </div>
            {formData.city && (
              <div className="side-summary-item">
                <span>رسوم المدينة:</span>
                <strong>{formData.cityFee > 0 ? `+${formData.cityFee}` : 'مجاني'}</strong>
              </div>
            )}
            {formData.discount > 0 && !formData.isManualTotal && (
              <div className="side-summary-item">
                <span>الخصم الإداري:</span>
                <strong style={{color: '#e74c3c'}}>{formData.discount}%</strong>
              </div>
            )}
            <div className="side-summary-item total">
              <span>الإجمالي:</span>
              <span>{formData.totalAmount} ر.س</span>
            </div>
            {formData.paidAmount > 0 && (
              <>
                <div className="side-summary-item">
                  <span>المدفوع:</span>
                  <span style={{color: '#166534'}}>{formData.paidAmount} ر.س</span>
                </div>
                <div className="side-summary-item" style={{borderTop: '1px solid #e2e8f0', paddingTop: '10px', marginTop: '10px'}}>
                  <span>المتبقي:</span>
                  <strong style={{color: '#b91c1c'}}>{Math.max(0, formData.totalAmount - formData.paidAmount)} ر.س</strong>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

        <div className="card-footer">
          {step > 1 ? (
            <button className="btn-secondary" onClick={() => setStep(step - 1)} disabled={loading}>رجوع</button>
          ) : <div></div>}
          
          {step < 4 ? (
            <button className="btn-primary" style={{width: 'auto'}} onClick={() => setStep(step + 1)} disabled={!formData.customerName || !formData.phone}>التالي</button>
          ) : (
            <button className="btn-primary" style={{width: 'auto'}} onClick={handleSubmit} disabled={loading}>
              {loading ? 'جاري الإرسال...' : 'اعتماد الحجز'}
            </button>
          )}
        </div>
    </div>
  </div>
  );
}

export default StaffBookingForm;
